// The ranked list of what matters most (CER-179): the outcomes a person is
// working toward, first to last. The database keeps the list and its rules
// (public.outcomes, reorder_outcomes); the rules both apps share for showing
// and changing it are here, once. It imports nothing but types, each by its
// full path, so Deno can load it as it is.

import type { ContainerRow } from './types/index.ts';

/** One outcome, as public.outcomes stores it. Its position is its rank among the active ones, 1 first. */
export interface Outcome {
  id: string;
  user_id: string;
  title: string;
  /** What it means and why it matters: the text a scoring step reads. */
  description: string;
  position: number;
  retired_at: string | null;
  created_at: string;
  updated_at: string;
}

export const OUTCOME_SELECT = 'id, user_id, title, description, position, retired_at, created_at, updated_at';
/** The limits the database enforces, so a form can say so before sending. */
export const OUTCOME_TITLE_MAX = 120;
export const OUTCOME_DESCRIPTION_MAX = 1000;
/** What the screens say the list is for. */
export const OUTCOMES_GUIDANCE = 'Everything you are working toward, most important first. Say in a sentence or two what each means and why it matters.';

/** The active outcomes, first to last. */
export function rankedOutcomes(outcomes: readonly Outcome[]): Outcome[] {
  return outcomes.filter((outcome) => outcome.retired_at === null).sort((a, b) => a.position - b.position || a.id.localeCompare(b.id));
}

/** The retired ones, most recently retired first. */
export function retiredOutcomes(outcomes: readonly Outcome[]): (Outcome & { retired_at: string })[] {
  return outcomes
    .filter((outcome): outcome is Outcome & { retired_at: string } => outcome.retired_at !== null)
    .sort((a, b) => b.retired_at.localeCompare(a.retired_at));
}

/**
 * The order after moving one outcome up or down a place: every active id,
 * first to last, as reorder_outcomes takes it. Null when it cannot move (it
 * is already first or last, or is not in the list).
 */
export function movedOutcomeOrder(ranked: readonly Outcome[], id: string, direction: 'up' | 'down'): string[] | null {
  const from = ranked.findIndex((outcome) => outcome.id === id);
  const to = direction === 'up' ? from - 1 : from + 1;
  if (from < 0 || to < 0 || to >= ranked.length) return null;
  const ids = ranked.map((outcome) => outcome.id);
  [ids[from], ids[to]] = [ids[to], ids[from]];
  return ids;
}

/** Why a title or description cannot be saved; null when it can. The title is judged trimmed, as it is saved. */
export function outcomeProblem(title: string, description: string, others: readonly Outcome[]): string | null {
  const name = title.trim();
  if (name === '') return 'An outcome needs a title.';
  if (name.length > OUTCOME_TITLE_MAX) return `The title is at most ${OUTCOME_TITLE_MAX} characters.`;
  if (description.length > OUTCOME_DESCRIPTION_MAX) return `The description is at most ${OUTCOME_DESCRIPTION_MAX} characters.`;
  if (rankedOutcomes(others).some((outcome) => outcome.title.toLowerCase() === name.toLowerCase())) return 'That outcome is already on the list.';
  return null;
}

/**
 * Candidates for the list, drawn from what the person already keeps: their
 * projects and areas that are not archived and are not already an outcome by
 * name. Projects first, each group by name.
 */
export function outcomeCandidates(containers: readonly ContainerRow[], outcomes: readonly Outcome[]): { kind: 'project' | 'area'; name: string }[] {
  const taken = new Set(rankedOutcomes(outcomes).map((outcome) => outcome.title.toLowerCase()));
  const pick = (kind: 'project' | 'area') =>
    containers
      .filter((c) => c.kind === kind && c.archived_at === null && !taken.has(c.name.trim().toLowerCase()))
      .map((c) => ({ kind, name: c.name.trim() }))
      .sort((a, b) => a.name.localeCompare(b.name));
  return [...pick('project'), ...pick('area')];
}
